import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useConfirmDialog } from "@/components/ui/confirm-dialog";
import { useToast } from "@/contexts/ToastContext";
import { catalogApi, type CatalogItemCopy } from "@/lib/api/catalog";
import { WithdrawCopyDialog } from "@/components/catalog/WithdrawCopyDialog";
import { Archive, RotateCcw, Trash2 } from "lucide-react";

interface CopyLifecycleActionsProps {
  item: CatalogItemCopy;
  canEdit: boolean;
  canDelete: boolean;
  onUpdated: (updated: CatalogItemCopy) => void;
  onRemoved: (itemId: string) => void;
  size?: "sm" | "default";
}

export function CopyLifecycleActions({
  item,
  canEdit,
  canDelete,
  onUpdated,
  onRemoved,
  size = "sm",
}: CopyLifecycleActionsProps) {
  const toast = useToast();
  const { confirm, ConfirmDialog: ConfirmDialogComponent } = useConfirmDialog();
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isWithdrawn = item.statusCode === "WITHDRAWN" || Boolean(item.withdrawnAt);

  const handleWithdraw = async (reason: string) => {
    setWithdrawing(true);
    try {
      const updated = await catalogApi.withdrawCopy(item.id, reason);
      onUpdated({ ...item, ...updated });
      toast.success(`Copy #${item.copyNumber} withdrawn.`);
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to withdraw copy.");
      throw err;
    } finally {
      setWithdrawing(false);
    }
  };

  const handleReactivate = async () => {
    setReactivating(true);
    try {
      const updated = await catalogApi.reactivateCopy(item.id);
      onUpdated({ ...item, ...updated });
      toast.success(`Copy #${item.copyNumber} reactivated.`);
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to reactivate copy.");
    } finally {
      setReactivating(false);
    }
  };

  const handleDelete = () => {
    confirm({
      title: "Delete Copy",
      description: (
        <div className="space-y-2">
          <p>
            Permanently delete copy <span className="font-mono font-medium">{item.barcode}</span>?
          </p>
          <p className="text-sm text-gray-500">
            This only succeeds when the copy has no circulation history. This action cannot be
            undone.
          </p>
        </div>
      ),
      variant: "danger",
      confirmLabel: "Delete Copy",
      onConfirm: async () => {
        setDeleting(true);
        try {
          await catalogApi.deleteCopy(item.id);
          onRemoved(item.id);
          toast.success(`Copy #${item.copyNumber} deleted.`);
        } catch (err: any) {
          toast.error(err.response?.data?.message || "Cannot delete this copy.");
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  if (!canEdit && !canDelete) return null;

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {canEdit && !isWithdrawn && (
          <Button
            type="button"
            variant="outline"
            size={size}
            className="gap-1 text-amber-700 hover:text-amber-800 hover:bg-amber-50 hover:border-amber-200"
            onClick={() => setWithdrawOpen(true)}
            disabled={withdrawing}
          >
            <Archive className="w-3.5 h-3.5" />
            Withdraw
          </Button>
        )}
        {canEdit && isWithdrawn && (
          <Button
            type="button"
            variant="outline"
            size={size}
            className="gap-1"
            onClick={handleReactivate}
            disabled={reactivating}
          >
            <RotateCcw className="w-3.5 h-3.5" />
            {reactivating ? "Reactivating..." : "Reactivate"}
          </Button>
        )}
        {canDelete && (
          <Button
            type="button"
            variant="outline"
            size={size}
            className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50 hover:border-red-200"
            onClick={handleDelete}
            disabled={deleting}
          >
            <Trash2 className="w-3.5 h-3.5" />
            Delete
          </Button>
        )}
      </div>
      <WithdrawCopyDialog
        open={withdrawOpen}
        onOpenChange={setWithdrawOpen}
        barcode={item.barcode}
        loading={withdrawing}
        onConfirm={handleWithdraw}
      />
      <ConfirmDialogComponent />
    </>
  );
}
